import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkle, ArrowRight, CheckCircle, FileText, Download } from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';
import { aiAPI } from '../lib/api';
import { ScheduleExpertButton } from './CalendlyModal';
import { toast } from 'sonner';

/**
 * Hero Proposal Composer — the centerpiece of the homepage.
 * 3-step flow:
 *  1) User types project description
 *  2) AI captures structured fields via /api/ai/chat
 *  3) Generate proposal via /api/ai/evaluate, then redirect to /proposal/:id
 */
export default function HeroProposalComposer({ centered = false }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('intake'); // 'intake' | 'qualifying' | 'collected' | 'generating' | 'done'
  const [input, setInput] = useState('');
  const [transcript, setTranscript] = useState([]);
  const [fields, setFields] = useState({});
  const [thinking, setThinking] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [error, setError] = useState(null);
  const sessionIdRef = React.useRef(`hero-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  const placeholderExamples = [
    'I need a WhatsApp bot for my restaurant — menu, orders, payment, and pickup reminders',
    'Build me a B2B SaaS dashboard for tracking field sales teams',
    'I want an AI agent that reads invoices and posts them to NetSuite',
    'A mobile app for booking yoga classes with payments and waitlists',
    'A programmatic SEO site for 5000 city-pages for my home-services business',
  ];
  const [phIdx, setPhIdx] = useState(0);
  useEffect(() => {
    if (phase !== 'intake') return;
    const t = setInterval(() => setPhIdx(i => (i + 1) % placeholderExamples.length), 3500);
    return () => clearInterval(t);
  }, [phase]);

  const sendToAI = async (userText) => {
    setThinking(true);
    setError(null);
    const newTranscript = [...transcript, { role: 'user', text: userText }];
    setTranscript(newTranscript);
    try {
      const res = await aiAPI.chat(
        sessionIdRef.current,
        userText,
        newTranscript.slice(-10).map(m => ({ role: m.role, text: m.text }))
      );
      const { reply, fields: f = {}, ready = false } = res.data || {};
      const merged = { ...fields, ...f };
      setFields(merged);
      setTranscript(prev => [...prev, { role: 'assistant', text: reply || 'Got it!' }]);
      if (ready) {
        setPhase('collected');
      } else {
        setPhase('qualifying');
      }
    } catch (err) {
      setError("Hmm, I couldn't reach the AI. Please try again.");
    } finally {
      setThinking(false);
    }
  };

  const handleFirstSubmit = (e) => {
    e?.preventDefault();
    if (!input.trim() || thinking) return;
    const text = input.trim();
    setInput('');
    sendToAI(text);
  };

  const handleFollowup = (e) => {
    e?.preventDefault();
    if (!input.trim() || thinking) return;
    const text = input.trim();
    setInput('');
    sendToAI(text);
  };

  const handleGenerate = async () => {
    if (phase === 'generating') return;
    setPhase('generating');
    try {
      const payload = {
        name: fields.name || 'Prospective Client',
        email: fields.email,
        country: fields.country || 'Global',
        description: fields.description || transcript.filter(m => m.role === 'user').map(m => m.text).join(' '),
        budget_range: fields.budget_range || null,
        timeline: fields.timeline || null,
        project_type: fields.project_type || null,
        source: 'website',
      };
      const res = await aiAPI.evaluate(payload);
      const id = res.data?.lead_id;
      setLeadId(id);
      setPhase('done');
      toast.success('Your AI proposal is ready!');
    } catch (err) {
      setError("Couldn't generate the proposal. Please try the full intake.");
      setPhase('collected');
    }
  };

  const capabilityPills = [
    { label: 'SaaS MVP', prompt: 'Build me a B2B SaaS MVP with auth, billing, and an admin dashboard' },
    { label: 'WhatsApp Bot', prompt: 'I need a WhatsApp bot for my business — catalog, orders, payments and reminders' },
    { label: 'E-commerce App', prompt: 'Build an e-commerce store with product catalog, cart, checkout and order tracking' },
    { label: 'AI Agent', prompt: 'I want an AI agent that automates a business workflow end to end' },
    { label: 'Mobile App', prompt: 'A mobile app for my customers with login, bookings and payments' },
    { label: 'Dashboard', prompt: 'A real-time analytics dashboard pulling data from my existing systems' },
    { label: 'Website', prompt: 'A modern marketing website with CMS and SEO built in' },
    { label: 'ERP/CRM Automation', prompt: 'Automate my ERP/CRM workflows — approvals, invoices, and data sync' },
  ];

  // INTAKE state — large input field
  if (phase === 'intake') {
    return (
      <motion.form
        onSubmit={handleFirstSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative max-w-3xl ${centered ? 'mx-auto w-full' : ''}`}
        data-testid="hero-proposal-composer"
      >
        <div className={`text-xs font-semibold tracking-[0.2em] uppercase mb-3 flex items-center gap-2 ${centered ? 'justify-center' : ''}`} style={{ color: '#FB923C' }}>
          <Sparkle weight="fill" size={14} /> AI Proposal Generator · No signup required
        </div>
        <div className="relative rounded-2xl border-2 transition-shadow focus-within:shadow-2xl" style={{ borderColor: '#1E293B', background: '#0F172A' }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleFirstSubmit(e); }
            }}
            placeholder={placeholderExamples[phIdx]}
            rows={3}
            className="w-full bg-transparent text-base lg:text-lg p-5 outline-none resize-none placeholder:text-slate-500"
            style={{ color: '#F8FAFC' }}
            data-testid="hero-proposal-input"
            autoFocus
          />
          <div className="flex items-center justify-between px-5 pb-4">
            <div className="text-xs" style={{ color: '#94A3B8' }}>
              Press <kbd className="px-1.5 py-0.5 rounded text-[10px] font-mono" style={{ background: '#1E293B' }}>↵</kbd> to send
            </div>
            <button
              type="submit"
              disabled={!input.trim()}
              className="h-10 px-5 rounded-md font-semibold flex items-center gap-1.5 transition-all disabled:opacity-40"
              style={{ background: '#EA580C', color: '#fff' }}
              data-testid="hero-proposal-submit"
            >
              Generate Proposal <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div className={`mt-4 flex flex-wrap gap-2 ${centered ? 'justify-center' : ''}`} data-testid="capability-pills">
          {capabilityPills.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setInput(p.prompt)}
              className="px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all hover:scale-105"
              style={{ borderColor: '#334155', background: 'rgba(30,41,59,0.6)', color: '#CBD5E1' }}
              data-testid={`capability-pill-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className={`mt-4 text-sm flex items-center gap-1.5 ${centered ? 'justify-center' : ''}`} style={{ color: '#94A3B8' }}>
          <CheckCircle weight="fill" size={14} style={{ color: '#22C55E' }} />
          AI evaluates scope, timeline, and cost · Delivered in 60 seconds · Free PDF
        </p>
        <p className={`mt-2 text-sm flex items-center gap-1.5 ${centered ? 'justify-center' : ''}`} style={{ color: '#94A3B8' }}>
          Prefer to talk?{' '}
          <ScheduleExpertButton className="font-semibold underline hover:no-underline" style={{ color: '#FB923C' }} testId="hero-schedule-expert">
            Schedule a meeting with our Expert
          </ScheduleExpertButton>
        </p>
      </motion.form>
    );
  }

  // QUALIFYING / COLLECTED state — chat view
  if (phase === 'qualifying' || phase === 'collected' || phase === 'generating') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`max-w-3xl ${centered ? 'mx-auto text-left' : ''}`}
        data-testid="hero-proposal-chat"
      >
        <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: '#1E293B', background: '#0F172A' }}>
          {/* Header */}
          <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: '#1E293B' }}>
            <div className="flex items-center gap-2 text-sm" style={{ color: '#F8FAFC' }}>
              <Sparkle weight="fill" size={14} style={{ color: '#EA580C' }} />
              <span className="font-semibold">XceedAgents AI</span>
              <span className="text-xs" style={{ color: '#94A3B8' }}>· qualifying your project</span>
            </div>
            <button
              onClick={() => { setPhase('intake'); setTranscript([]); setFields({}); }}
              className="text-xs hover:underline"
              style={{ color: '#94A3B8' }}
              data-testid="hero-restart"
            >
              Restart
            </button>
          </div>

          {/* Messages */}
          <div className="px-5 py-4 max-h-[300px] overflow-y-auto space-y-3" data-testid="hero-transcript">
            {transcript.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] px-3.5 py-2 rounded-xl text-sm leading-relaxed"
                  style={m.role === 'user'
                    ? { background: '#EA580C', color: '#fff' }
                    : { background: '#1E293B', color: '#F8FAFC' }}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 rounded-xl" style={{ background: '#1E293B' }}>
                  <div className="flex gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer / input or generate CTA */}
          <div className="px-5 py-4 border-t" style={{ borderColor: '#1E293B' }}>
            {phase === 'collected' || phase === 'generating' ? (
              <button
                onClick={handleGenerate}
                disabled={phase === 'generating'}
                className="w-full h-11 rounded-md font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-60"
                style={{ background: '#EA580C', color: '#fff' }}
                data-testid="hero-generate-proposal"
              >
                {phase === 'generating' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating your detailed proposal…
                  </>
                ) : (
                  <>
                    <Sparkle weight="fill" size={16} />
                    Generate My Detailed Proposal
                  </>
                )}
              </button>
            ) : (
              <form onSubmit={handleFollowup} className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your reply…"
                  className="flex-1 bg-transparent text-sm px-3 py-2 rounded outline-none border placeholder:text-slate-500"
                  style={{ borderColor: '#334155', color: '#F8FAFC' }}
                  data-testid="hero-followup-input"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!input.trim() || thinking}
                  className="h-9 px-4 rounded font-semibold transition-all disabled:opacity-40"
                  style={{ background: '#EA580C', color: '#fff' }}
                  data-testid="hero-followup-send"
                >
                  Send
                </button>
              </form>
            )}
            {error && <p className="mt-3 text-sm text-red-400" data-testid="hero-error">{error}</p>}
          </div>
        </div>
      </motion.div>
    );
  }

  // DONE — show success + CTAs
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`max-w-3xl ${centered ? 'mx-auto text-left' : ''}`}
      data-testid="hero-proposal-done"
    >
      <div className="rounded-2xl border-2 p-7" style={{ borderColor: '#22C55E', background: '#0F172A' }}>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle weight="fill" size={22} style={{ color: '#22C55E' }} />
          <div className="font-heading text-2xl font-bold" style={{ color: '#F8FAFC' }}>Your AI proposal is ready!</div>
        </div>
        <p className="text-sm leading-relaxed mb-5" style={{ color: '#94A3B8' }}>
          We've prepared a detailed proposal with scope, timeline, recommended stack, and budget. Open the workspace to view it, download the PDF, or share it with your team.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate(`/proposal/${leadId}`)}
            className="h-11 px-6 rounded-md font-semibold flex items-center gap-2"
            style={{ background: '#EA580C', color: '#fff' }}
            data-testid="hero-view-proposal"
          >
            <FileText size={16} weight="fill" /> View Full Proposal
          </button>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/leads/${leadId}/proposal-pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="h-11 px-6 rounded-md font-semibold flex items-center gap-2 border"
            style={{ borderColor: '#1E293B', color: '#F8FAFC' }}
            data-testid="hero-download-pdf"
          >
            <Download size={16} /> Download PDF
          </a>
          <Link to="/login" className="h-11 px-6 rounded-md font-semibold flex items-center gap-2" style={{ color: '#FB923C' }} data-testid="hero-create-workspace">
            Save to my workspace →
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
